import { Component, KeyboardEvent } from 'react'
import * as Sentry from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'

import config from './config'
import { isMobileApp, setIsMobileAppInAppBrowserFromQuery } from 'utils/helpers'
import { testingSkeleton } from 'hooks/useSkeleton'
import routes, { RenderRoutes } from 'routes'

import { setupFullStory } from './fullStory'
import { removeHasProKey } from 'modules/enrollment/helpers'
import { SessionContext } from 'context'
setupFullStory(config)

const root = window as any
const document = root.document

setIsMobileAppInAppBrowserFromQuery()

isMobileApp() && document.body.classList.add('inside-mobile-app')
testingSkeleton() && document.body.classList.add('testing-skeleton')

// SENTRY INIT/ERROR CAPTURE
Sentry.init({
  environment: config.env,
  release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
  dsn: config.SENTRY_DSN,
  integrations: [new BrowserTracing()]
})

class App extends Component {
  static contextType = SessionContext

  async componentDidMount() {
    if (!this.context?.isAuthenticated) {
      removeHasProKey()
    }

    const userJson = this.context?.user

    if (root.dataLayer) {
      root.dataLayer.push({
        app: isMobileApp() ? 'mobile-app' : 'not-mobile-app'
      })
    }

    // Identify user in FullStory
    if (config.env === 'production' && userJson) {
      const { firstName, lastName, email, uuid } = userJson
      const fullStory = (window as any).FS

      if (fullStory) {
        fullStory.identify(uuid, {
          displayName: `${firstName} ${lastName}`,
          appType: isMobileApp() ? 'mobile' : 'web',
          email
        })
      }
    }

    document.addEventListener('keydown', this.handleFirstTab)
  }

  componentDidCatch(error: any, errorInfo: any): void {
    Sentry.withScope((scope) => {
      Object.keys(errorInfo).forEach((key) => {
        scope.setExtra(key, errorInfo[key])
      })

      Sentry.captureException(error)
    })

    if (super.componentDidCatch) super.componentDidCatch(error, errorInfo)
  }

  componentWillUnmount(): void {
    removeHasProKey()
  }

  handleFirstTab = (e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      document.body.classList.add('user-is-tabbing')

      document.removeEventListener('keydown', this.handleFirstTab)
      document.addEventListener('mousedown', this.handleMouseDownOnce)
    }
  }

  handleMouseDownOnce = () => {
    document.body.classList.remove('user-is-tabbing')

    document.removeEventListener('mousedown', this.handleMouseDownOnce)
    document.addEventListener('keydown', this.handleFirstTab)
  }

  render() {
    return <RenderRoutes routes={routes} />
  }
}

export default App
